import { logAppleAuth } from '../utils/appleAuthDiagnostics';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { signInWithRedirect } from '@aws-amplify/auth';
import { appleSignInEnabled } from '../aws-exports';
import { Form, Button, Alert, InputGroup } from 'react-bootstrap';
import './LoginPage.css';
import { normalizeLoginUsername } from '../utils/normalizeSignupPhone';
import eyeIcon from '../assets/icons/eye.svg';
import eyeOffIcon from '../assets/icons/eye-off.svg';

export function LoginPage() {
  const { login, confirmMfa, loading, error } = useAuth();
  const location = useLocation();
  const signupSuccess = location.state?.signupSuccess;
  const [appleLoading, setAppleLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const handleAppleSignIn = async () => {
    setFormError(null);
    setAppleLoading(true);
    try {
      logAppleAuth('redirect_start');
      await signInWithRedirect({ provider: 'Apple' });
    } catch (error) {
      logAppleAuth('redirect_failure', { error });
      setFormError('Unable to start Apple sign-in. Please try again.');
      setAppleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      alert('Please fill in all fields');
      return;
    }

    setFormError(null);
    let username;
    try { username = normalizeLoginUsername(email); } catch (err) { setFormError(err.message); return; }
    const result = await login(username, password);
    if (result.challengeType === 'CONFIRM_SIGN_UP') {
      window.location.href = `/verify?username=${encodeURIComponent(username)}`;
      return;
    }

    if (result.mfaRequired) {
      setMfaRequired(true);
      setMfaChallenge(result.challengeType);
    } else if (result.success) {
      window.location.href = '/dashboard';
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    const result = await confirmMfa(mfaCode);
    if (result.success) {
      window.location.href = '/dashboard';
    }
  };

  return (
    <div className="login-container">
      <div className="tg-auth-card">
        <div className="tg-auth-brand">
          <div className="tg-auth-brand-mark">T</div>
          <span className="tg-auth-brand-name">Tegrat Auto</span>
        </div>

        {mfaRequired ? (
          <>
            <h2 className="tg-auth-title">Verify your identity</h2>
            <p className="tg-auth-subtitle">
              {mfaChallenge === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
                ? 'Enter the 6-digit code from your authenticator app.'
                : mfaChallenge === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
                ? 'Enter the authentication code sent to your phone via SMS.'
                : mfaChallenge === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
                ? 'Enter the authentication code sent to your email.'
                : 'Enter the authentication code sent to you.'}
            </p>

            <Form onSubmit={handleMfaSubmit}>
              <Form.Group className="mb-3" controlId="mfaCode">
                <Form.Label>Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="6-digit code"
                  disabled={loading}
                  required
                  autoFocus
                />
              </Form.Group>

              {(formError || error) && <Alert variant="danger">{formError || error}</Alert>}

              <div className="d-grid gap-2">
                <Button variant="primary" type="submit" disabled={loading} size="lg">
                  {loading ? 'Verifying…' : 'Verify'}
                </Button>
              </div>
            </Form>
          </>
        ) : (
          <>
            <h2 className="tg-auth-title">Sign in</h2>
            <p className="tg-auth-subtitle">Welcome back to your account</p>

            {signupSuccess && (
              <Alert variant="success">Account created! You can now sign in.</Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email or signup phone number</Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com or (555) 000-0000"
                  disabled={loading}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="password">
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    type="button"
                  >
                    <img src={showPassword ? eyeIcon : eyeOffIcon} alt="" style={{ width: 18, height: 18 }} />
                  </Button>
                </InputGroup>
              </Form.Group>

              {(formError || error) && <Alert variant="danger">{formError || error}</Alert>}

              <div className="d-grid gap-2">
                <Button variant="primary" type="submit" disabled={loading} size="lg">
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
              </div>
            </Form>

            {appleSignInEnabled && (
              <div className="d-grid mt-3">
                <Button variant="dark" className="tg-apple-sign-in" type="button" size="lg"
                  disabled={loading || appleLoading} onClick={handleAppleSignIn}>
                  {appleLoading ? 'Connecting to Apple…' : 'Continue with Apple'}
                </Button>
              </div>
            )}
            <hr className="tg-auth-divider" />
            <div className="tg-auth-footer">
              <Link to="/forgot-password">Forgot password?</Link>
              <span className="tg-auth-dot">·</span>
              <Link to="/signup">Create an account</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
